// src/services/leonardoAIService.ts
import axios from 'axios';

// Types for Leonardo AI
export type LeonardoModelId = 
  | 'leonardo-creative' 
  | 'leonardo-diffusion-xl' 
  | 'leonardo-pro' 
  | 'leonardo-marketing-pro';

export interface LeonardoGenerationOptions {
  prompt: string;
  modelId: LeonardoModelId;
  width: number;
  height: number;
  numImages?: number;
  seed?: number;
  guidanceScale?: number;
  organizationId?: string;
  organizationName?: string;
  organizationBranding?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
  };
}

export interface LeonardoContentGenerationOptions {
  prompt: string;
  contentType: 'social' | 'email' | 'blog' | 'ad';
  tone: 'formal' | 'casual' | 'professional' | 'friendly' | 'persuasive';
  industry?: string;
  targetAudience?: string;
  wordCount?: number;
  organizationId?: string;
  organizationName?: string;
}

export interface LeonardoGenerationResponse {
  id: string;
  images: Array<{
    url: string;
    id: string;
  }>;
  status: 'COMPLETE' | 'PENDING';
}

export interface LeonardoContentResponse {
  id: string;
  content: string;
  status: 'COMPLETE' | 'PENDING';
}

// Check if we have a Leonardo API key
export const leonardoApiInfo = {
  keyExists: !!process.env.REACT_APP_LEONARDO_AI_API_KEY,
  keyLength: process.env.REACT_APP_LEONARDO_AI_API_KEY?.length || 0,
  firstChars: process.env.REACT_APP_LEONARDO_AI_API_KEY?.substring(0, 4) || 'none'
};

// API key for Leonardo AI
const API_KEY = process.env.REACT_APP_LEONARDO_AI_API_KEY || '';
const API_URL = 'https://cloud.leonardo.ai/api/rest/v1';

/**
 * Generate visual content using Leonardo AI
 */
export async function generateMarketingAsset(options: LeonardoGenerationOptions): Promise<string> {
   if (!API_KEY) {
      console.error('Leonardo AI API key is missing');
      return generatePlaceholderImage(options.width, options.height);
   }

   try {
      // Set default values
      const numImages = options.numImages || 1;
      const seed = options.seed || Math.floor(Math.random() * 2147483647);
      const guidanceScale = options.guidanceScale || 7;

      // Enhance prompt with organization context if available
      let enhancedPrompt = options.prompt;
      if (options.organizationName) {
         enhancedPrompt += `, branded for ${options.organizationName}`;
      }

      if (options.organizationBranding?.primaryColor) {
         enhancedPrompt += `, using color ${options.organizationBranding.primaryColor}`;
      }

      // Generation API call
      const generationResponse = await axios.post(
         `${API_URL}/generations`,
         {
            prompt: enhancedPrompt,
            modelId: options.modelId,
            width: options.width,
            height: options.height,
            num_images: numImages,
            seed,
            guidance_scale: guidanceScale,
            public: false,
         },
         {
            headers: {
               Authorization: `Bearer ${API_KEY}`,
               'Content-Type': 'application/json',
            },
         }
      );

      if (!generationResponse.data || !generationResponse.data.generationId) {
         throw new Error('Failed to initiate generation');
      }

      const generationId = generationResponse.data.generationId;

      // Poll for results
      let attemptsLeft = 30; // Maximum 5 minutes (30 attempts × 10 seconds)
      while (attemptsLeft > 0) {
         await new Promise((resolve) => setTimeout(resolve, 10000)); // Wait 10 seconds

         const resultResponse = await axios.get(`${API_URL}/generations/${generationId}`, {
            headers: {
               Authorization: `Bearer ${API_KEY}`,
            },
         });

         if (resultResponse.data.status === 'COMPLETE') {
            if (resultResponse.data.images && resultResponse.data.images.length > 0) {
               return resultResponse.data.images[0].url;
            }
         }

         attemptsLeft--;
      }

      throw new Error('Timed out waiting for image generation');
   } catch (error) {
      console.error('Leonardo AI API error:', error);
      return generatePlaceholderImage(options.width, options.height);
   }
}

/**
 * Generate marketing content using Leonardo AI
 */
export async function generateMarketingContent(
   options: LeonardoContentGenerationOptions
): Promise<string> {
   if (!API_KEY) {
      console.error('Leonardo AI API key is missing');
      return generatePlaceholderContent(options.contentType);
   }

   try {
      // Enhance prompt with organization context if available
      let enhancedPrompt = options.prompt;
      if (options.organizationName) {
         enhancedPrompt += `, for the organization ${options.organizationName}`;
      }

      if (options.industry) {
         enhancedPrompt += `, in the ${options.industry} industry`;
      }

      if (options.targetAudience) {
         enhancedPrompt += `, targeting ${options.targetAudience}`;
      }

      // Mock API call for content generation (Leonardo might not have a text endpoint)
      // In a real implementation, you would use their actual API endpoints
      const contentResponse = await mockLeonardoContentAPI(
         enhancedPrompt,
         options.contentType,
         options.tone,
         options.wordCount || 100
      );

      return contentResponse;
   } catch (error) {
      console.error('Leonardo AI content generation error:', error);
      return generatePlaceholderContent(options.contentType);
   }
}

/**
 * Generate placeholder content while we wait for actual Leonardo AI implementation
 */
async function mockLeonardoContentAPI(
  prompt: string,
  contentType: string,
  tone: string,
  wordCount: number
): Promise<string> {
  // This would be replaced with actual API call
  const contentTemplates = {
    social: `Check out our latest offering! ${prompt} #exciting #newlaunch`,
    email: `Dear Valued Customer,\n\nWe're excited to share with you our latest update. ${prompt}\n\nBest regards,\nThe Team`,
    blog: `# Exciting News\n\nToday, we're thrilled to announce our newest development. ${prompt}\n\n## What This Means For You\n\nThis innovative approach will transform how you interact with our platform.`,
    ad: `NEW! ${prompt} - Don't miss out on this amazing opportunity. Limited time offer!`
  };
  
  return contentTemplates[contentType as keyof typeof contentTemplates] || 
    `Content related to: ${prompt}. Generated with ${tone} tone for ${contentType}.`;
}

/**
 * Generate a placeholder image when Leonardo AI is not available
 */
function generatePlaceholderImage(width: number, height: number): string {
  // Create a data URL for a placeholder
  return `https://via.placeholder.com/${width}x${height}/1976D2/FFFFFF?text=Leonardo+AI+Placeholder`;
}

/**
 * Generate placeholder content when Leonardo AI is not available
 */
function generatePlaceholderContent(contentType: string): string {
  return `This is placeholder ${contentType} content. Please configure your Leonardo AI API key to generate real content.`;
}

/**
 * Get available Leonardo AI models
 */
export const getLeonardoModels = () => [
  { value: 'leonardo-creative', label: 'Leonardo Creative' },
  { value: 'leonardo-diffusion-xl', label: 'Leonardo Diffusion XL' },
  { value: 'leonardo-pro', label: 'Leonardo Pro' },
  { value: 'leonardo-marketing-pro', label: 'Leonardo Marketing Pro' }
];
